import { AxiosError } from 'axios';
import instance from '../config/axios';
import { stringify } from 'qs';
import { PaginationState } from '@tanstack/react-table';
import {
  DoctorProductResponse,
  PatientOrderResponse,
  PatientProductResponse,
  ProductTypeFileds,
} from '../interface/interface';

export const getPaginatedDoctors = async (
  accessToken: string,
  page: number,
  limit: number,
  search?: string,
) => {
  try {
    const response = await instance({
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      method: 'GET',
      url: `api/v1/doctor/list/all?${stringify({ page, limit, search })}`,
    });

    return response.data;
  } catch (e) {
    console.log('Error while retriving doctors', e);
    return (e as AxiosError).response?.status;
  }
};

export const updateDoctor = async (accessToken: string, data: unknown) => {
  try {
    const response = await instance({
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      url: '/api/v1/doctor/update',
      method: 'PUT',
      data,
    });

    return response.status;
  } catch (e) {
    console.log('Unable to update doctor', e);
    return (e as AxiosError).response?.status as number;
  }
};

export const createDoctor = async (
  accessToken: string,
  data: unknown,
): Promise<number> => {
  try {
    const response = await instance({
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      url: '/api/v1/doctor/create',
      method: 'POST',
      data,
    });

    return response.status;
  } catch (e) {
    console.log('Unable to create doctor', e);
    return (e as AxiosError).response?.status as number;
  }
};

export const getSingleDoctor = async (
  accessToken: string,
  doctorId: number,
) => {
  try {
    const response = await instance({
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      url: `/api/v1/doctor/${doctorId}`,
      method: 'GET',
    });

    return response.data;
  } catch (e) {
    console.log('Unable to get doctor', {
      cause: (e as AxiosError).stack,
      doctorId,
    });
    return (e as AxiosError).response?.status as number;
  }
};

export const getProducts = async (
  accessToken: string,
  productType: ProductTypeFileds,
  search: string,
  sort: {
    sortByCategory: Array<string>;
    sortByCondition: Array<string>;
    sortByFormulation: Array<string>;
  },
  pagination: PaginationState,
  filterBy?: string,
): Promise<{
  data: Array<DoctorProductResponse>;
  total: number;
  productMetas: {
    categories: string[];
    formulations: string[];
  };
}> => {
  try {
    const response = await instance({
      method: 'GET',
      url: `/api/v1/doctor/products/${productType}/list?${stringify({
        search: search,
        page: pagination.pageIndex,
        limit: pagination.pageSize,
        filter: [filterBy],
        sort,
      })}`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return {
      data: response?.data?.productsDetails,
      total: response?.data?.total,
      productMetas: response.data.productMetas,
    };
  } catch (err) {
    console.log('Unable to get products', {
      cause: (err as AxiosError).stack,
    });
    return {
      data: [],
      total: 0,
      productMetas: { categories: [], formulations: [] },
    };
  }
};

export const getPatientOrders = async (
  accessToken: string,
  patientId: string,
  search: string,
  startMonth: string,
  endMonth: string,
): Promise<PatientOrderResponse> => {
  try {
    const response = await instance({
      method: 'GET',
      url: `/api/v1/doctor/patient/${patientId}/orders?${stringify({
        search,
        startMonth,
        endMonth,
      })}`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (err) {
    console.log('Unable to get patient orders', {
      cause: (err as AxiosError).stack,
    });
    return {
      total: 0,
      data: [],
    };
  }
};

export const getPatientProducts = async (
  accessToken: string,
  patientId: string,
): Promise<PatientProductResponse> => {
  try {
    const response = await instance({
      method: 'GET',
      url: `/api/v1/doctor/patient/products/${patientId}/`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (err) {
    console.log('Unable to get products', {
      cause: (err as AxiosError).stack,
    });
    return {
      total: 0,
      data: [],
    };
  }
};

export const getPatientMonthlyLimit = async (
  accessToken: string,
  patientId: string,
  startMonth: string,
  endMonth: string,
) => {
  try {
    const response = await instance({
      method: 'GET',
      url: `/api/v1/doctor/patient/products/montlylimit/${patientId}/?${stringify(
        {
          startMonth,
          endMonth,
        },
      )}`,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (err) {
    console.log('Unable to get products', {
      cause: (err as AxiosError).stack,
    });
    return {
      total: 0,
      data: [],
    };
  }
};
